import React, { useEffect, useState } from "react"
import { Col, Row, Modal } from "react-bootstrap"
import Select from "react-select"
import { DatePicker, Table } from "antd"
import { useNavigate, useLocation } from "react-router-dom"
import Navbar from "../../Components/Navbar/Navbar"
import {
  firstDayOfMonth,
  lastDayOfMonth,
  formatDateNoTime,
  numberFormat,
  selectDropdownStyle,
  selectDropdownStyleWithPortal,
  formatNum,
  formatAmount,
} from "../../Helpers/Utils/Common"
import { getBankReconciliation } from "../../Helpers/apiCalls/SalesReport/SalesReportApi"
// import Table from "../../Components/TableTemplate/Table"
import { searchBank } from "../../Helpers/apiCalls/Manage/Banks"
import "./BankReconciliation.css"

const { RangePicker } = DatePicker

export default function BankReconciliation() {
  const [inactive, setInactive] = useState(true)
  const [banksData, setBanksData] = useState([])
  const [filterConfig, setFilterConfig] = useState({
    date_from: firstDayOfMonth(),
    date_to: lastDayOfMonth(),
    bank_id: "1",
  })
  const [showLoader, setShowLoader] = useState(false)
  const [tableData, setTableData] = useState([])
  const [selectedEntry, setSelectedEntry] = useState(null) // State for selected entry
  const [showModal, setShowModal] = useState(false) // State for modal visibility
  const location = useLocation()
  const navigate = useNavigate()

  const getDocumentPath = (entry) => {
    if (!entry?.id) return null
    const ref = entry.ref_no || ""
    if (ref.startsWith("SALES INVOICE NO.")) return `/projectinvoice/print/${entry.id}` // view (PrintProjectInvoice)
    if (ref.startsWith("BANK PAYMENT NO.")) return `/se/paysuppliers/view/${entry.id}/bank` // view (ViewPaySupplier - bank)
    if (ref.startsWith("PETTY CASH NO."))
      return entry.entry_type === "Credit"
        ? `/pettycash/cashout/${entry.id}` // view (PettyCashCashOutView)
        : `/pettycash/cashin/${entry.id}` // view (PettyCashCashInView)
    return null // BANK TRANSFER NO., adjustments, and fund transactions: no document page
  }

  const getTransactionCategory = (ref_no) => {
    const ref = ref_no || ""
    if (ref.startsWith("SALES INVOICE NO.")) return "sales_invoice"
    if (ref.startsWith("BANK PAYMENT NO.")) return "bank_payment"
    if (ref.startsWith("PETTY CASH NO.")) return "petty_cash"
    if (ref.startsWith("BANK TRANSFER NO.")) return "bank_transfer"
    if (ref.startsWith("Interest -") || ref.startsWith("Withholding Tax -")) return "bank_adjustment"
    if (ref === "ADD FUNDS" || ref === "WITHDRAW FUNDS") return "fund_transaction"
    return "other"
  }

  var columns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      width: window.innerWidth <= 425 ? 80 : 150,
      ellipsis: true,
    },
    {
      title: "Transaction",
      dataIndex: "ref_no",
      key: "ref_no",
      width: window.innerWidth <= 425 ? 120 : 200,
      ellipsis: true,
      render: (text, record) => (
        <span
          className={`${
            record.ref_no === "Opening" || record.ref_no === "Closing"
              ? "bold"
              : ""
          }`}
          style={{ fontSize: window.innerWidth <= 425 ? '0.7rem' : '1rem' }}
        >
          {text}
        </span>
      ),
    },
    {
      title: "Debit",
      dataIndex: "debit",
      key: "debit",
      align: "right",
      width: window.innerWidth <= 425 ? 90 : 120,
      render: (text, record) => (
        <span
          className={`${
            record.ref_no === "Opening" || record.ref_no === "Closing"
              ? "bold"
              : ""
          }`}
          style={{ fontSize: window.innerWidth <= 425 ? '0.7rem' : '1rem' }}
        >
          {text}
        </span>
      ),
    },
    {
      title: "Credit",
      dataIndex: "credit",
      key: "credit",
      align: "right",
      width: window.innerWidth <= 425 ? 90 : 120,
      render: (text, record) => (
        <span
          className={`${
            record.ref_no === "Opening" || record.ref_no === "Closing"
              ? "bold"
              : ""
          }`}
          style={{ fontSize: window.innerWidth <= 425 ? '0.7rem' : '1rem' }}
        >
          {text}
        </span>
      ),
    },
    {
      title: "Balance",
      dataIndex: "balance",
      key: "balance",
      align: "right",
      width: window.innerWidth <= 425 ? 100 : 130,
      render: (text, record) => (
        <span
          className={`${
            record.ref_no === "Opening" || record.ref_no === "Closing"
              ? "bold"
              : ""
          }`}
          style={{ fontSize: window.innerWidth <= 425 ? '0.7rem' : '1rem' }}
        >
          {text}
        </span>
      ),
    },
  ]

  async function fetchTableData() {
    setShowLoader(true)
    setTableData([])

    const response = await getBankReconciliation(filterConfig)

    if (response?.data) {
      var data = response?.data?.bank_reconciliation
      var summary = response.data.bank_summary
      const formattedData = [
        {
          date: "",
          ref_no: "Opening",
          debit: "0.00",
          credit: "0.00",
          balance: summary.previous_balance === "0.00" ? "0.00" : formatAmount(parseFloat(summary.previous_balance)),
        },
      ]

      data?.forEach((entry, index) => {
        formattedData.push({
          key: index,
          id: entry.id, 
          source: entry.source, // 'project_invoice' | 'supplies_expense' | 'petty_cash' | 'bank_transfer' 
          entry_type: entry.type, // 'Credit' | 'Debit'
          amount: entry.paid_amount, // raw amount for the preview
          date: entry.date ? formatDateNoTime(entry.date) : " ",
          ref_no: entry.reference_no,
          bank_name: entry.bank_name || "",
          // detail fields for the transaction preview modal — only some are
          // populated per row depending on transaction type (see getTransactionCategory)
          payee_name: entry.payee_name || "",
          particulars: entry.particulars || "",
          project_name: entry.project_name || "",
          payment_type: entry.payment_type || "",
          cheque_number: entry.cheque_number || "",
          reference_number: entry.reference_number || "",
          remarks: entry.remarks || "",
          requested_by_name: entry.requested_by_name || "",
          expense_type_name: entry.expense_type_name || "",
          bank_from_name: entry.bank_from_name || "",
          bank_to_name: entry.bank_to_name || "",
          transaction_fee: entry.transaction_fee || 0,
          po_breakdown: entry.po_breakdown || [], // [{ label, amount }] - purchase orders settled
          pe_breakdown: entry.pe_breakdown || [], // [{ label, amount }] - project expenses settled
          debit:
            entry.type === "Debit"
              ? formatAmount(parseFloat(entry.paid_amount))
              : "0.00",
          credit:
            entry.type === "Credit"
              ? formatAmount(parseFloat(entry.paid_amount))
              : "0.00",
          balance: formatAmount(entry.balance),
        })
      })

      formattedData.push({
        date: "",
        ref_no: "Closing",
        debit: summary.total_debit === 0 ? "0.00" : formatAmount(summary.total_debit),
        credit: summary.total_credit === 0 ? "0.00" : formatAmount(summary.total_credit),
        balance: summary.total_balance === "0.00" ? "0.00" : formatAmount(parseFloat(summary.total_balance)),
      })

      setTableData(formattedData)
    }

    setShowLoader(false)
  }

  async function fetchBanks() {
    setBanksData([])
    setShowLoader(true)
    const response = await searchBank()

    if (response) {
      let result = response.data.data.data.map((bank) => {
        return {
          label: bank.name,
          value: bank.id,
        }
      })
      setBanksData(result)
    }
  }

  useEffect(() => {
    fetchTableData()
  }, [filterConfig, location])

  useEffect(() => {
    fetchBanks()
  }, [])

  const handleRowClick = (record) => {
    if (!record.id || record.ref_no === "Opening" || record.ref_no === "Closing") return
    setSelectedEntry(record)
    setShowModal(true)
  }

  const closeModal = () => {
    setShowModal(false)
    setSelectedEntry(null)
  }

  return (
    <div>
      <div className="page">
        <Navbar
          onCollapse={(inactive) => {
            setInactive(inactive);
          }}
          active={"FINANCIAL REPORT"}
        />
      </div>
      <div className={`manager-container ${inactive ? "inactive" : "active"}`}>
        <Row className="mb-4">
          <Col xs={6}>
            <h1 className="page-title">Bank Reconciliation</h1>
          </Col>
          <Col xs={6} className="d-flex justify-content-end"></Col>
        </Row>

        <div className="tab-content">
          <div className="ms-3 PO-filters PI-filters d-flex flex-wrap mb-4 gap-3">
            <Select
              options={banksData}
              className="dropsearch-filter px-0 py-0 me-2 mb-2"
              classNamePrefix="react-select"
              styles={selectDropdownStyleWithPortal}
              placeholder="Select Bank"
              value={banksData.find(
                (bank) => bank.value === filterConfig.bank_id
              )}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              onChange={(e) =>
                setFilterConfig((prev) => ({
                  ...prev,
                  bank_id: e.value,
                }))
              }
            />
            <RangePicker
              className="mb-2"
              style={{
                width: window.innerWidth <= 768 ? '100%' : 'auto'
              }}
              placeholder={[
                filterConfig.date_from ?? firstDayOfMonth(),
                filterConfig.date_to ?? lastDayOfMonth(),
              ]}
              onChange={(e) => {
                if (e) {
                  setFilterConfig((prev) => ({
                    ...prev,
                    date_from: e[0].format("YYYY-MM-DD"),
                    date_to: e[1].format("YYYY-MM-DD"),
                  }))
                } else {
                  setFilterConfig((prev) => ({
                    ...prev,
                    date_from: firstDayOfMonth(),
                    date_to: lastDayOfMonth(),
                  }))
                }
              }}
            />
          </div>
          <div className="px-3 ant-table-container">
            <div className="table-responsive">
              <Table
                pagination={false}
                columns={columns}
                dataSource={tableData}
                showLoader={showLoader}
                scroll={{ x: 600 }}
                size="small"
                onRow={(record) => ({
                  onClick: () => handleRowClick(record),
                  style: { cursor: record.id ? "pointer" : "default" },
                })}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Transaction preview */}
      <Modal show={showModal} onHide={closeModal} size="lg" centered>
        <Modal.Header closeButton />
        <Modal.Body>
          <div className="col-sm-12">
            <div className="col-sm-12">
              <span className="custom-modal-body-title">TRANSACTION PREVIEW</span>
            </div>
            <div className="modal-cont justify-content-center">
              {selectedEntry && (
                <>
                  <Row className="nc-modal-custom-row mt-3">
                    <Col xs={5}>REFERENCE NO.</Col>
                    <Col xs={7}>{selectedEntry.ref_no}</Col>
                  </Row>
                  <Row className="nc-modal-custom-row mt-2">
                    <Col xs={5}>DATE</Col>
                    <Col xs={7}>{selectedEntry.date}</Col>
                  </Row>
                  <Row className="nc-modal-custom-row mt-2">
                    <Col xs={5}>TYPE</Col>
                    <Col xs={7}>{selectedEntry.entry_type}</Col>
                  </Row>
                  <Row className="nc-modal-custom-row mt-2">
                    <Col xs={5}>AMOUNT</Col>
                    <Col xs={7}>
                      {formatAmount(parseFloat(selectedEntry.amount || 0))}
                    </Col>
                  </Row>
                  {selectedEntry.bank_name && (
                    <Row className="nc-modal-custom-row mt-2">
                      <Col xs={5}>BANK</Col>
                      <Col xs={7}>{selectedEntry.bank_name}</Col>
                    </Row>
                  )}

                  {/* SALES INVOICE (project invoice payment) */}
                  {getTransactionCategory(selectedEntry.ref_no) === "sales_invoice" && (
                    <>
                      {selectedEntry.project_name && (
                        <Row className="nc-modal-custom-row mt-2">
                          <Col xs={5}>PROJECT/CLIENT</Col>
                          <Col xs={7}>{selectedEntry.project_name}</Col>
                        </Row>
                      )}
                      {selectedEntry.payment_type && (
                        <Row className="nc-modal-custom-row mt-2">
                          <Col xs={5}>PAYMENT METHOD</Col>
                          <Col xs={7}>{selectedEntry.payment_type}</Col>
                        </Row>
                      )}
                      {(selectedEntry.cheque_number || selectedEntry.reference_number) && (
                        <Row className="nc-modal-custom-row mt-2">
                          <Col xs={5}>CHEQUE/REF. NO.</Col>
                          <Col xs={7}>
                            {selectedEntry.cheque_number || selectedEntry.reference_number}
                          </Col>
                        </Row>
                      )}
                      {selectedEntry.remarks && (
                        <Row className="nc-modal-custom-row mt-2">
                          <Col xs={5}>REMARKS</Col>
                          <Col xs={7}>{selectedEntry.remarks}</Col>
                        </Row>
                      )}
                    </>
                  )}

                  {/* BANK PAYMENT (se_bank_slip) */}
                  {getTransactionCategory(selectedEntry.ref_no) === "bank_payment" && (
                    <>
                      {selectedEntry.payee_name && (
                        <Row className="nc-modal-custom-row mt-2">
                          <Col xs={5}>PAID TO</Col>
                          <Col xs={7}>{selectedEntry.payee_name}</Col>
                        </Row>
                      )}
                      {selectedEntry.particulars && (
                        <Row className="nc-modal-custom-row mt-2">
                          <Col xs={5}>PARTICULARS</Col>
                          <Col xs={7}>{selectedEntry.particulars}</Col>
                        </Row>
                      )}
                      {selectedEntry.po_breakdown?.length > 0 && (
                        <Row className="nc-modal-custom-row mt-2">
                          <Col xs={5}>PO(S) SETTLED</Col>
                          <Col xs={7}>
                            {selectedEntry.po_breakdown.map((inv, i) => (
                              <div key={i}>
                                {inv.label} — {formatAmount(parseFloat(inv.amount || 0))}
                              </div>
                            ))}
                          </Col>
                        </Row>
                      )}
                      {selectedEntry.pe_breakdown?.length > 0 && (
                        <Row className="nc-modal-custom-row mt-2">
                          <Col xs={5}>PE(S) SETTLED</Col>
                          <Col xs={7}>
                            {selectedEntry.pe_breakdown.map((inv, i) => (
                              <div key={i}>
                                {inv.label} — {formatAmount(parseFloat(inv.amount || 0))}
                              </div>
                            ))}
                          </Col>
                        </Row>
                      )}
                      {selectedEntry.transaction_fee > 0 && (
                        <Row className="nc-modal-custom-row mt-2">
                          <Col xs={5}>TRANSACTION FEE</Col>
                          <Col xs={7}>
                            {formatAmount(parseFloat(selectedEntry.transaction_fee))}
                          </Col>
                        </Row>
                      )}
                    </>
                  )}

                  {/* PETTY CASH (fund-in) */}
                  {getTransactionCategory(selectedEntry.ref_no) === "petty_cash" && (
                    <>
                      {selectedEntry.requested_by_name && (
                        <Row className="nc-modal-custom-row mt-2">
                          <Col xs={5}>REQUESTED BY</Col>
                          <Col xs={7}>{selectedEntry.requested_by_name}</Col>
                        </Row>
                      )}
                      {selectedEntry.expense_type_name && (
                        <Row className="nc-modal-custom-row mt-2">
                          <Col xs={5}>EXPENSE TYPE</Col>
                          <Col xs={7}>{selectedEntry.expense_type_name}</Col>
                        </Row>
                      )}
                      {(selectedEntry.particulars || selectedEntry.remarks) && (
                        <Row className="nc-modal-custom-row mt-2">
                          <Col xs={5}>PARTICULARS/REMARKS</Col>
                          <Col xs={7}>
                            {selectedEntry.particulars || selectedEntry.remarks}
                          </Col>
                        </Row>
                      )}
                    </>
                  )}

                  {/* BANK TRANSFER */}
                  {getTransactionCategory(selectedEntry.ref_no) === "bank_transfer" && (
                    <>
                      {(selectedEntry.bank_from_name || selectedEntry.bank_to_name) && (
                        <Row className="nc-modal-custom-row mt-2">
                          <Col xs={5}>FROM BANK → TO BANK</Col>
                          <Col xs={7}>
                            {selectedEntry.bank_from_name || "—"} → {selectedEntry.bank_to_name || "—"}
                          </Col>
                        </Row>
                      )}
                      {selectedEntry.remarks && (
                        <Row className="nc-modal-custom-row mt-2">
                          <Col xs={5}>REMARKS</Col>
                          <Col xs={7}>{selectedEntry.remarks}</Col>
                        </Row>
                      )}
                    </>
                  )}

                  {/* BANK MONTHLY ADJUSTMENT (interest / withholding tax) & FUND TRANSACTION (add/withdraw funds) */}
                  {["bank_adjustment", "fund_transaction"].includes(
                    getTransactionCategory(selectedEntry.ref_no)
                  ) &&
                    selectedEntry.remarks && (
                      <Row className="nc-modal-custom-row mt-2">
                        <Col xs={5}>REMARKS</Col>
                        <Col xs={7}>{selectedEntry.remarks}</Col>
                      </Row>
                    )}
                </>
              )}
            </div>
            <div className="col-sm-12 mt-4 d-flex justify-content-end">
              {selectedEntry && getDocumentPath(selectedEntry) && (
                <button
                  className="button-primary me-3"
                  onClick={() => {
                    const path = getDocumentPath(selectedEntry)
                    closeModal()
                    navigate(path)
                  }}
                >
                  Open Document
                </button>
              )}
              <button className="button-tertiary" onClick={closeModal}>
                Close
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  )
}